import type {
  DefinedUseQueryResult,
  UseQueryResult,
} from '@tanstack/react-query';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { searchProductReviews } from '@jane/search/data-access';
import type { AlgoliaReview, CurrentSort, Feeling } from '@jane/search/types';
import type { QueryBody } from '@jane/shared/data-access';

type ProductFeelingsQueryBody = QueryBody<InternalFeeling[]>;
type QueryOptions = Pick<ProductFeelingsQueryBody, 'initialData'>;

interface UseProductFeelingsArgs {
  options?: QueryOptions;
  productId: number;
}

export type InternalFeeling = {
  count: number;
  feeling: Feeling;
};

interface GetAlgoliaProductReviewsResult {
  count: number;
  facets: Record<string, Record<string, number>> | undefined;
  hits: AlgoliaReview[];
  page: number;
  totalPages: number;
}

type GetProductReviewParams = {
  currentSort?: CurrentSort;
  hasCommentsFilter: boolean;
  page?: number;
};

/** Query */

const getProductFeelingsSummary = async (productId: number) => {
  const { facets } = await searchProductReviews({
    searchOptions: {
      facetFilters: [`product_id:${productId}`],
      facets: ['feelings'],
      page: 0,
    },
  });

  const rawFeelingsCount = facets ? (facets['feelings'] ?? {}) : {};
  const sortedFeelings = Object.keys(rawFeelingsCount)
    .map<InternalFeeling>((feelingName) => ({
      count: rawFeelingsCount[feelingName],
      feeling: feelingName.toLowerCase() as Feeling,
    }))
    .filter(({ count }) => count > 0)
    .sort(({ count: aCount }, { count: bCount }) => bCount - aCount);

  return sortedFeelings;
};

const getProductReview = async (
  productId: number,
  { hasCommentsFilter, page: pageOption, currentSort }: GetProductReviewParams
): Promise<GetAlgoliaProductReviewsResult> => {
  const numericFilters = ['hidden = 0'];

  const { facets, hits, nbHits, nbPages, page } = await searchProductReviews({
    currentSort,
    searchOptions: {
      facetFilters: [`product_id:${productId}`],
      facets: ['has_comment'],
      hitsPerPage: 3,
      numericFilters: hasCommentsFilter
        ? [...numericFilters, 'has_comment = 1']
        : numericFilters,
      page: pageOption,
    },
  });

  return {
    count: nbHits ?? 0,
    facets,
    hits,
    page: page ?? 0,
    totalPages: nbPages ?? 0,
  };
};

/** Query Body Overloads */

export function getProductFeelingsQueryBody(
  productId: UseProductFeelingsArgs['productId'],
  options?: Omit<QueryOptions, 'initialData'> & {
    initialData?: () => undefined;
  }
): ProductFeelingsQueryBody;

export function getProductFeelingsQueryBody(
  productId: UseProductFeelingsArgs['productId'],
  options?: Omit<QueryOptions, 'initialData'> & {
    initialData?: InternalFeeling[] | (() => InternalFeeling[]);
  }
): ProductFeelingsQueryBody;

/** Query Body */

export function getProductFeelingsQueryBody(
  productId: UseProductFeelingsArgs['productId'],
  options?: object
): ProductFeelingsQueryBody {
  return {
    queryFn: async () => await getProductFeelingsSummary(productId),
    queryKey: ['useProductFeelings', productId],
    ...options,
  };
}

/** Hook overloads */
export function useProductFeelings(
  productId: UseProductFeelingsArgs['productId'],
  options?: Omit<QueryOptions, 'initialData'> & {
    initialData?: () => undefined;
  }
): UseQueryResult<InternalFeeling[], unknown>;

export function useProductFeelings(
  productId: UseProductFeelingsArgs['productId'],
  options?: Omit<QueryOptions, 'initialData'> & {
    initialData?: InternalFeeling[] | (() => InternalFeeling[]);
  }
): DefinedUseQueryResult<InternalFeeling[], unknown>;

/** Hook */

export function useProductFeelings(
  productId: UseProductFeelingsArgs['productId'],
  options?: object
) {
  return useQuery(getProductFeelingsQueryBody(productId, options));
}

interface UseProductReviewsOptionProps {
  currentSort?: CurrentSort;
  hasCommentsFilter?: boolean;
}

export const useProductReviews = (
  productId: number,
  { hasCommentsFilter = true, currentSort }: UseProductReviewsOptionProps = {
    hasCommentsFilter: true,
  }
) =>
  useInfiniteQuery({
    getNextPageParam: ({
      page,
      totalPages,
    }: GetAlgoliaProductReviewsResult) => {
      if (page < totalPages) {
        return page + 1;
      }
      return undefined;
    },
    queryFn: async ({ pageParam }: { pageParam?: number }) =>
      getProductReview(productId, {
        hasCommentsFilter,
        page: pageParam,
        currentSort,
      }),
    queryKey: ['reviews', productId, hasCommentsFilter, currentSort],
    staleTime: 5 * 60 * 1000,
  });
